import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionTag } from "./Reveal";
import { fetchTestimonials, toAbsolute } from "@/lib/api";

export default function Testimonials() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => { fetchTestimonials(lang).then((r) => setItems(r.data)).catch(() => {}); }, [lang]);
  if (items.length === 0) return null;

  return (
    <section id="testimonials" className="relative section-pad overflow-hidden" data-testid="testimonials-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 30%, rgba(10,30,78,0.4), transparent 55%)" }} />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <Reveal><SectionTag>{t.testimonials.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}><h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 mb-4">{t.testimonials.title}</h2></Reveal>
          <Reveal delay={0.1}><p className="text-slate-400 text-base sm:text-lg leading-relaxed">{t.testimonials.subtitle}</p></Reveal>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((tm, i) => (
            <Reveal key={tm.id} delay={(i % 3) * 0.1}>
              <motion.div whileHover={{ y: -6 }} className="relative card-surface rounded-3xl p-7 h-full flex flex-col" dir="auto" data-testid={`testimonial-${i}`}>
                <Quote size={40} className="text-primary opacity-20 mb-3" />
                <p className="text-slate-200 leading-relaxed flex-1 text-base">{tm.quote}</p>
                <div className="flex items-center gap-1 mt-5 mb-4">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={16} className={s < (tm.rating || 5) ? "text-primary" : "text-slate-600"} fill={s < (tm.rating || 5) ? "currentColor" : "none"} />
                  ))}
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  {tm.avatar ? (
                    <img src={toAbsolute(tm.avatar)} alt={tm.name} loading="lazy" className="h-12 w-12 rounded-full object-cover" />
                  ) : (
                    <div className="h-12 w-12 rounded-full grid place-items-center font-bold text-[#04120a]" style={{ background: "var(--primary)" }}>{tm.name?.[0]}</div>
                  )}
                  <div>
                    <div className="font-bold">{tm.name}</div>
                    <div className="text-xs text-slate-400">{tm.role}{tm.company ? ` · ${tm.company}` : ""}</div>
                  </div>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
